import {
  type RefObject,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Check } from "lucide-react";

import type { DocumentViewportValue } from "@/features/document-viewport/types";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import { formatMeasurementHeight } from "@/lib/geometry";
import { TOOL_IDS } from "@/viewport-tools/measurement";

import {
  computeValue,
  formatDisplayedMeasurement,
  getMeasurementLabelPosition,
  getMeasurementLeaderAnchor,
  hexToRgba,
  shouldShowMeasurementLeader,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-helpers";
import {
  MEASUREMENT_GEOMETRY_TYPES,
  type DraftMeasurementState,
  type MeasurementAnnotation,
  type MeasurementGeometry,
  type MeasurementOverlayRecord,
  type PolygonGeometry,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-types";
import type { InteractionState, ResizeHandle } from "@/viewport-tools/measurement/overlay/interactions/types";

function isOpenPathTool(tool: MeasurementOverlayRecord["tool"] | DraftMeasurementState["tool"]) {
  return tool === TOOL_IDS.line || tool === TOOL_IDS.polyline;
}

function getMeasurementLabelScale(
  baseFontSize: number,
  viewportScale: number,
  textLocked: boolean
): { baseFontSize: number; scale: number } {
  // Labels are positioned in screen coordinates.
  // - Locked: keep a fixed screen size.
  // - Unlocked: scale with page zoom.
  // Locked: scale = 1
  // Unlocked: scale = viewportScale
  if (textLocked) {
    return { baseFontSize, scale: 1 };
  }
  return { baseFontSize, scale: viewportScale > 0 ? viewportScale : 1 };
}

function MeasurementLabelButton({
  labelRef,
  label,
  labelFontSize,
  labelScale,
  labelPosition,
  screenRect,
  borderColor,
  isSelected,
  measurement,
  annotation,
  displayedValue,
  startMeasurementLabelMove,
  openContextMenu,
}: {
  labelRef: RefObject<HTMLButtonElement | null>;
  label: string;
  labelFontSize: number;
  labelScale: number;
  labelPosition: { x: number; y: number };
  screenRect?: { x: number; y: number };
  borderColor: string;
  isSelected: boolean;
  measurement: MeasurementOverlayRecord;
  annotation?: MeasurementAnnotation;
  displayedValue: number;
  startMeasurementLabelMove: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation | undefined
  ) => void;
  openContextMenu: (event: ReactMouseEvent, measurement: MeasurementOverlayRecord) => void;
}) {
  const offsetX = screenRect?.x ?? 0;
  const offsetY = screenRect?.y ?? 0;

  return (
    <button
      ref={labelRef}
      type="button"
      aria-label={`Move ${label} label`}
      className="absolute pointer-events-auto border bg-background/90 text-center font-black leading-tight shadow-sm"
      style={{
        fontSize: labelFontSize,
        left: labelPosition.x - offsetX,
        top: labelPosition.y - offsetY,
        padding: "0.22em 0.5em",
        borderRadius: "0.6em",
        lineHeight: 1.05,
        transform: `translate(-50%, -50%) scale(${labelScale})`,
        transformOrigin: "center center",
        borderColor,
        color: "var(--foreground)",
        cursor: isSelected ? "grab" : "pointer",
      }}
      onMouseDown={(event) => startMeasurementLabelMove(event, measurement._id, annotation)}
      onContextMenu={(event) => openContextMenu(event, measurement)}
    >
      <div>{label}</div>
      <div>
        {formatDisplayedMeasurement(measurement.measurementType, displayedValue)}{" "}
        {measurement.measurementType}
      </div>
    </button>
  );
}

interface PersistedMeasurementsLayerProps {
  viewport: DocumentViewportValue;
  measurements: MeasurementOverlayRecord[];
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[];
  previewAnnotations: Record<string, MeasurementAnnotation>;
  previewGeometries: Record<string, MeasurementGeometry | PolygonGeometry>;
  colorByTagId: Map<string, string>;
  labelByTagId: Map<string, string>;
  scale: MeasurementScale | null;
  textLocked?: boolean;
  textFontSize?: number;
  startMeasurementMove: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    geometry: MeasurementGeometry | PolygonGeometry
  ) => void;
  startMeasurementResize: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    handle: ResizeHandle,
    geometry: MeasurementGeometry
  ) => void;
  startMeasurementVertexDrag: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    vertexIndex: number,
    geometry: PolygonGeometry
  ) => void;
  openVertexContextMenu: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    vertexIndex: number
  ) => void;
  startMeasurementLabelMove: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation | undefined
  ) => void;
  openContextMenu: (
    event: ReactMouseEvent,
    measurement: MeasurementOverlayRecord
  ) => void;
}

function PersistedMeasurementItem({
  viewport,
  measurement,
  isSelected,
  previewAnnotation,
  previewGeometry,
  colorByTagId,
  labelByTagId,
  scale,
  textLocked,
  textFontSize,
  startMeasurementMove,
  startMeasurementResize,
  startMeasurementVertexDrag,
  openVertexContextMenu,
  startMeasurementLabelMove,
  openContextMenu,
}: {
  viewport: DocumentViewportValue;
  measurement: MeasurementOverlayRecord;
  isSelected: boolean;
  previewAnnotation?: MeasurementAnnotation;
  previewGeometry?: MeasurementGeometry | PolygonGeometry;
  colorByTagId: Map<string, string>;
  labelByTagId: Map<string, string>;
  scale: MeasurementScale | null;
  textLocked?: boolean;
  textFontSize?: number;
  startMeasurementMove: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    geometry: MeasurementGeometry | PolygonGeometry
  ) => void;
  startMeasurementResize: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    handle: ResizeHandle,
    geometry: MeasurementGeometry
  ) => void;
  startMeasurementVertexDrag: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    vertexIndex: number,
    geometry: PolygonGeometry
  ) => void;
  openVertexContextMenu: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    vertexIndex: number
  ) => void;
  startMeasurementLabelMove: (
    event: ReactMouseEvent,
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation | undefined
  ) => void;
  openContextMenu: (
    event: ReactMouseEvent,
    measurement: MeasurementOverlayRecord
  ) => void;
}) {
  const color = colorByTagId.get(String(measurement.tagId ?? "")) ?? "#0EA5E9";
  const label = labelByTagId.get(String(measurement.tagId ?? "")) ?? "Tag";
  const fillColor = hexToRgba(color, isSelected ? 0.14 : 0.1);
  const hatchColor = hexToRgba(color, 0.22);
  const geometry = previewGeometry ?? measurement.geometry;
  const annotation = previewAnnotation ?? measurement.annotation;
  const displayedValue =
    scale && previewGeometry
      ? computeValue(
          measurement.tool,
          measurement.measurementType,
          previewGeometry,
          measurement.height,
          scale.pixelsPerFoot,
          scale.pageAspectRatio
        )
      : measurement.value;
  const labelPositionPage = getMeasurementLabelPosition(geometry, annotation);
  const isOpenPath = geometry.type === MEASUREMENT_GEOMETRY_TYPES.poly && isOpenPathTool(measurement.tool);
  const leaderAnchorPage = getMeasurementLeaderAnchor(geometry, labelPositionPage, {
    closed: !isOpenPath,
  });
  const labelAnchor = viewport.transforms.pageToScreenPoint(leaderAnchorPage);
  const labelPosition = viewport.transforms.pageToScreenPoint(labelPositionPage);
  const showLeader = shouldShowMeasurementLeader(annotation);
  // Default to unlocked (false) so text scales with document by default
  const { baseFontSize: labelFontSize, scale: labelScale } = getMeasurementLabelScale(
    textFontSize ?? 13,
    viewport.camera.zoom,
    textLocked ?? false
  );
  const labelRef = useRef<HTMLButtonElement | null>(null);
  const [labelSize, setLabelSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const node = labelRef.current;
    if (!node) {
      return;
    }
    const nextSize = { width: node.offsetWidth, height: node.offsetHeight };
    setLabelSize((current) =>
      current.width === nextSize.width && current.height === nextSize.height ? current : nextSize
    );
  }, [label, displayedValue, measurement.measurementType, labelFontSize]);

  const leaderTarget = resolveLeaderTargetPoint({
    anchor: labelAnchor,
    labelCenter: labelPosition,
    labelWidth: labelSize.width * Math.max(0.01, Math.abs(labelScale)),
    labelHeight: labelSize.height * Math.max(0.01, Math.abs(labelScale)),
  });

  if (geometry.type === MEASUREMENT_GEOMETRY_TYPES.rect) {
    const rect = geometry;
    const screenRect = viewport.transforms.pageRectToScreenRect({
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
    });

    return (
      <div
        className="absolute pointer-events-none"
        style={{
          left: screenRect.x,
          top: screenRect.y,
          width: screenRect.width,
          height: screenRect.height,
        }}
      >
        <button
          type="button"
          className="absolute inset-0 pointer-events-auto rounded-none border-2 cursor-move"
          style={{
            borderColor: color,
            backgroundColor: fillColor,
            backgroundImage: `repeating-linear-gradient(135deg, ${hatchColor} 0 2px, transparent 2px 10px)`,
            boxShadow: isSelected ? `0 0 0 2px ${color}55` : `0 0 0 1px ${color}33`,
          }}
          onMouseDown={(event) => startMeasurementMove(event, measurement._id, rect)}
          onContextMenu={(event) => openContextMenu(event, measurement)}
        />
        {showLeader ? (
          <svg className="absolute inset-0 h-full w-full pointer-events-none overflow-visible">
            {renderLeaderArrow({
              startX: labelAnchor.x - screenRect.x,
              startY: labelAnchor.y - screenRect.y,
              endX: leaderTarget.x - screenRect.x,
              endY: leaderTarget.y - screenRect.y,
              color,
              strokeWidth: (annotation?.strokeWidth ?? 2) * 0.5,
            })}
          </svg>
        ) : null}
        <MeasurementLabelButton
          labelRef={labelRef}
          label={label}
          labelFontSize={labelFontSize}
          labelScale={labelScale}
          labelPosition={labelPosition}
          screenRect={screenRect}
          borderColor={color}
          isSelected={isSelected}
          measurement={measurement}
          annotation={annotation}
          displayedValue={displayedValue}
          startMeasurementLabelMove={startMeasurementLabelMove}
          openContextMenu={openContextMenu}
        />
        {isSelected
          ? (["nw", "ne", "sw", "se"] as const).map((handle) => {
              const handlePosition = {
                nw: { left: -6, top: -6, cursor: "nwse-resize" },
                ne: { right: -6, top: -6, cursor: "nesw-resize" },
                sw: { left: -6, bottom: -6, cursor: "nesw-resize" },
                se: { right: -6, bottom: -6, cursor: "nwse-resize" },
              }[handle];
              return (
                <button
                  key={handle}
                  type="button"
                  aria-label={`Resize ${label} measurement ${measurement._id} (${handle})`}
                  className="absolute pointer-events-auto size-3 rounded-full border-2 border-background bg-sky-500"
                  style={handlePosition}
                  onMouseDown={(event) =>
                    startMeasurementResize(event, measurement._id, handle, rect)
                  }
                />
              );
            })
          : null}
      </div>
    );
  }

  const poly = geometry;
  const screenVertices = poly.vertices.map((v) =>
    viewport.transforms.pageToScreenPoint(v)
  );

  let pathData = screenVertices
    .map((v, i) => `${i === 0 ? "M" : "L"} ${v.x} ${v.y}`)
    .join(" ");
  const isClosed = !isOpenPath && screenVertices.length >= 3;
  if (isClosed) {
    pathData += " Z";
  }

  const handlePolyMouseDown = (event: Parameters<typeof startMeasurementMove>[0]) =>
    startMeasurementMove(event, measurement._id, poly);

  return (
    <div className="absolute inset-0 pointer-events-none">
      <svg className="absolute inset-0 w-full h-full">
        {isOpenPathTool(measurement.tool) ? (
          <path
            d={pathData}
            fill="none"
            stroke="transparent"
            strokeWidth="14"
            strokeLinecap="round"
            className="cursor-move pointer-events-auto"
            onMouseDown={handlePolyMouseDown}
            onContextMenu={(event) => openContextMenu(event, measurement)}
          />
        ) : null}
        <path
          d={pathData}
          fill={isClosed ? fillColor : "none"}
          stroke={color}
          strokeWidth={annotation?.strokeWidth ?? 2}
          className={
            isOpenPathTool(measurement.tool)
              ? "pointer-events-none"
              : "cursor-move pointer-events-auto"
          }
          strokeDasharray={isSelected ? "none" : "5,5"}
          onMouseDown={
            isOpenPathTool(measurement.tool) ? undefined : handlePolyMouseDown
          }
          onContextMenu={(event) => openContextMenu(event, measurement)}
        />
        {showLeader ? (
          renderLeaderArrow({
            startX: labelAnchor.x,
            startY: labelAnchor.y,
            endX: leaderTarget.x,
            endY: leaderTarget.y,
            color,
            strokeWidth: (annotation?.strokeWidth ?? 2) * 0.5,
          })
        ) : null}
      </svg>
      {isSelected
        ? screenVertices.map((point, index) => (
            <button
              key={`${measurement._id}-vertex-${index}`}
              type="button"
              aria-label={`Adjust ${label} vertex ${index + 1}`}
              className="absolute pointer-events-auto size-3 rounded-full border border-white bg-sky-500 shadow-[0_0_0_1px_rgba(2,132,199,0.55)]"
              style={{
                left: point.x,
                top: point.y,
                transform: "translate(-50%, -50%)",
                cursor: "move",
              }}
              onMouseDown={(event) => {
                if (event.button !== 0) {
                  return;
                }
                startMeasurementVertexDrag(event, measurement._id, index, poly);
              }}
              onContextMenu={(event) => openVertexContextMenu(event, measurement._id, index)}
            />
          ))
        : null}
      <MeasurementLabelButton
        labelRef={labelRef}
        label={label}
        labelFontSize={labelFontSize}
        labelScale={labelScale}
        labelPosition={labelPosition}
        borderColor={color}
        isSelected={isSelected}
        measurement={measurement}
        annotation={annotation}
        displayedValue={displayedValue}
        startMeasurementLabelMove={startMeasurementLabelMove}
        openContextMenu={openContextMenu}
      />
    </div>
  );
}

export function PersistedMeasurementsLayer({
  viewport,
  measurements,
  selectedMeasurementIds,
  previewAnnotations,
  previewGeometries,
  colorByTagId,
  labelByTagId,
  scale,
  textLocked,
  textFontSize,
  startMeasurementMove,
  startMeasurementResize,
  startMeasurementVertexDrag,
  openVertexContextMenu,
  startMeasurementLabelMove,
  openContextMenu,
}: PersistedMeasurementsLayerProps) {
  return (
    <>
      {measurements.map((measurement) => {
        const isSelected = selectedMeasurementIds.includes(measurement._id);
        return (
          <PersistedMeasurementItem
            key={measurement._id}
            viewport={viewport}
            measurement={measurement}
            isSelected={isSelected}
            previewAnnotation={previewAnnotations[String(measurement._id)]}
            previewGeometry={previewGeometries[String(measurement._id)]}
            colorByTagId={colorByTagId}
            labelByTagId={labelByTagId}
            scale={scale}
            textLocked={textLocked}
            textFontSize={textFontSize}
            startMeasurementMove={startMeasurementMove}
            startMeasurementResize={startMeasurementResize}
            startMeasurementVertexDrag={startMeasurementVertexDrag}
            openVertexContextMenu={openVertexContextMenu}
            startMeasurementLabelMove={startMeasurementLabelMove}
            openContextMenu={openContextMenu}
          />
        );
      })}
    </>
  );
}

export function DraftMeasurementPreviewLayer({
  viewport,
  drawingDraft,
  draftMeasurement,
  interaction,
  startDraftMove,
}: {
  viewport: DocumentViewportValue;
  drawingDraft: DraftMeasurementState | null;
  draftMeasurement: DraftMeasurementState | null;
  interaction: InteractionState | null;
  startDraftMove: (event: ReactMouseEvent, draft: DraftMeasurementState) => void;
}) {
  const draft = drawingDraft ?? draftMeasurement;
  if (!draft) {
    return null;
  }

  const color = "#0EA5E9";
  const fillColor = hexToRgba(color, 0.1);
  const hatchColor = hexToRgba(color, 0.22);

  if (draft.geometry.type === MEASUREMENT_GEOMETRY_TYPES.rect) {
    const rect = draft.geometry;
    const screenRect = viewport.transforms.pageRectToScreenRect({
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
    });

    return (
      <div
        className="absolute rounded-none border-2 border-dashed border-sky-500"
        style={{
          left: screenRect.x,
          top: screenRect.y,
          width: screenRect.width,
          height: screenRect.height,
          backgroundColor: fillColor,
          backgroundImage: `repeating-linear-gradient(135deg, ${hatchColor} 0 2px, transparent 2px 10px)`,
          pointerEvents: drawingDraft ? "none" : "auto",
          cursor: drawingDraft ? "default" : "move",
        }}
        onMouseDown={(event) => startDraftMove(event, draft)}
      >
        <div className="absolute inset-0 flex items-center justify-center p-3">
          <div
            className="text-center text-[13px] font-black leading-tight text-sky-900"
            style={{ textShadow: "0 1px 0 rgba(255,255,255,0.35)" }}
          >
            <div>
              {formatDisplayedMeasurement(draft.measurementType, draft.value)}{" "}
              {draft.measurementType}
            </div>
            {draft.height ? <div>Height: {formatMeasurementHeight(draft.height)}</div> : null}
          </div>
        </div>
      </div>
    );
  }

  const poly = draft.geometry;
  if (poly.vertices.length < 1) {
    return null;
  }

  const screenVertices = poly.vertices.map((v) =>
    viewport.transforms.pageToScreenPoint(v)
  );
  const lineFinishIndex =
    isOpenPathTool(draft.tool) && interaction?.mode === "drawing"
      ? Math.max(0, poly.vertices.length - 2)
      : -1;
  const vertexMarkers = screenVertices.map((point, index) => {
    const isPolygonStart =
      draft.tool === TOOL_IDS.polygon && index === 0 && poly.vertices.length >= 3;
    const isLineFinish =
      isOpenPathTool(draft.tool) &&
      index === lineFinishIndex &&
      poly.vertices.length >= 2;
    return (
      <div
        key={`vertex-${index}-${point.x}-${point.y}`}
        className="absolute pointer-events-none"
        style={{
          left: point.x,
          top: point.y,
          transform: "translate(-50%, -50%)",
        }}
      >
        <div className="size-2.5 rounded-full border border-white bg-sky-500 shadow-[0_0_0_1px_rgba(2,132,199,0.55)]" />
        {isPolygonStart || isLineFinish ? (
          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-emerald-500 text-white shadow-[0_0_0_1px_rgba(16,185,129,0.75)]">
            <Check className="size-2" />
          </div>
        ) : null}
      </div>
    );
  });

  if (poly.vertices.length < 2) {
    return <>{vertexMarkers}</>;
  }

  let pathData = screenVertices
    .map((v, i) => `${i === 0 ? "M" : "L"} ${v.x} ${v.y}`)
    .join(" ");
  const isClosed = !isOpenPathTool(draft.tool) && poly.vertices.length >= 3;
  if (isClosed) {
    pathData += " Z";
  }

  const labelPosition = viewport.transforms.pageToScreenPoint(
    getMeasurementLabelPosition(draft.geometry, draft.annotation)
  );

  return (
    <div className="absolute inset-0 pointer-events-none">
      <svg className="absolute inset-0 w-full h-full">
        <path
          d={pathData}
          fill={isClosed ? fillColor : "none"}
          stroke={color}
          strokeWidth="2"
          strokeDasharray="5,5"
          className={drawingDraft ? undefined : "pointer-events-auto"}
          style={{ cursor: drawingDraft ? "default" : "move" }}
          onMouseDown={(event) => startDraftMove(event, draft)}
        />
      </svg>
      {vertexMarkers}
      <div
        className="absolute text-center text-[13px] font-black leading-tight"
        style={{
          left: labelPosition.x,
          top: labelPosition.y,
          transform: "translate(-50%, -50%)",
          color: "#0c4a6e",
          textShadow: "0 1px 0 rgba(255,255,255,0.35)",
        }}
      >
        <div>
          {formatDisplayedMeasurement(draft.measurementType, draft.value)}{" "}
          {draft.measurementType}
        </div>
        {draft.height ? <div>Height: {formatMeasurementHeight(draft.height)}</div> : null}
      </div>
    </div>
  );
}

export type { PersistedMeasurementsLayerProps };

function renderLeaderArrow(args: {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  color: string;
  strokeWidth: number;
}) {
  const dx = args.endX - args.startX;
  const dy = args.endY - args.startY;
  const length = Math.hypot(dx, dy);
  if (length < 1) {
    return null;
  }

  const ux = dx / length;
  const uy = dy / length;
  const gap = 2;
  const arrowSize = 6;
  const shaftEndX = args.endX - ux * (gap + arrowSize + 1);
  const shaftEndY = args.endY - uy * (gap + arrowSize + 1);
  const arrowTipX = args.endX - ux * gap;
  const arrowTipY = args.endY - uy * gap;
  const perpX = -uy;
  const perpY = ux;
  const leftX = arrowTipX - ux * arrowSize + perpX * (arrowSize * 0.55);
  const leftY = arrowTipY - uy * arrowSize + perpY * (arrowSize * 0.55);
  const rightX = arrowTipX - ux * arrowSize - perpX * (arrowSize * 0.55);
  const rightY = arrowTipY - uy * arrowSize - perpY * (arrowSize * 0.55);

  return (
    <>
      <line
        x1={args.startX}
        y1={args.startY}
        x2={shaftEndX}
        y2={shaftEndY}
        stroke={args.color}
        strokeWidth={args.strokeWidth}
        className="pointer-events-none"
      />
      <path
        d={`M ${leftX} ${leftY} L ${arrowTipX} ${arrowTipY} L ${rightX} ${rightY} Z`}
        fill={args.color}
        stroke={args.color}
        strokeWidth={args.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="pointer-events-none"
      />
    </>
  );
}

function resolveLeaderTargetPoint(args: {
  anchor: { x: number; y: number };
  labelCenter: { x: number; y: number };
  labelWidth: number;
  labelHeight: number;
}) {
  const halfWidth = args.labelWidth / 2;
  const halfHeight = args.labelHeight / 2;
  if (halfWidth <= 0 || halfHeight <= 0) {
    return args.labelCenter;
  }

  const dx = args.anchor.x - args.labelCenter.x;
  const dy = args.anchor.y - args.labelCenter.y;
  const ratio = Math.max(Math.abs(dx) / halfWidth, Math.abs(dy) / halfHeight);
  if (!Number.isFinite(ratio) || ratio <= 0) {
    return args.labelCenter;
  }

  return {
    x: args.labelCenter.x + dx / ratio,
    y: args.labelCenter.y + dy / ratio,
  };
}
